import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Loader2, ChevronDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { mealTypeLabel } from '@/utils/format';
import type { MealType } from '@/types';

/**
 * «Часто ешь» — готовые продукты человека прямо на экране добавления еды.
 *
 * ЗАЧЕМ (ревизия Дневника 04.09.2026). Люди переписывают одно и то же
 * десятками раз: у Нади за 60 дней «Банан» 67 раз, «Фасоль» 62, «Финик» 60.
 * А в «Моих блюдах» всего 9 записей у 4 человек за всё время, и приёмов
 * из избранного за 30 дней — 6 из 1076. Истории не было на этом экране вовсе.
 *
 * ПОЧЕМУ СПИСОК, А НЕ ПОИСК (решение Славы): «поиск лишний раз нагружает,
 * надо начать думать, вспоминать. А список наглядно показывает и нужен
 * только один тап». Узнавание дешевле припоминания.
 *
 * ПОЧЕМУ КОНТЕКСТНЫЙ. Удлинять список нельзя — у Евгении 139 разных продуктов
 * за 30 дней, простыня нечитаема. Зато данные показали, что еда сильно
 * различается по приёмам: у Нади на завтрак финики и банан, на ужин томаты,
 * капуста и авокадо, пересечение почти нулевое. Поэтому список идёт под тип
 * приёма и сокращается сам, без единого действия человека.
 *
 * 💰 Плюс прямая экономия: набранный текстом продукт уходит в модель
 * на разбор (~0.09 ₽), тап по готовому — ноль вызовов.
 */

export type FrequentItem = {
  name: string;
  weight_g: number;
  calories: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;
  times: number;
  times_this_meal: number;
  last_at: string;
};

/** Свёрнуто — один ряд, чтобы не занимать экран. Развёрнуто — всё, что есть. */
const COLLAPSED = 6;

export function FrequentItems({
  mealType,
  onPick,
  hideNames = [],
}: {
  mealType: MealType;
  onPick: (item: FrequentItem) => void;
  /** Уже добавленные в этот приём — второй раз предлагать незачем */
  hideNames?: string[];
}) {
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [items, setItems] = useState<FrequentItem[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!profile?.id) return;
    // Берём с запасом: свёрнутый вид покажет первые, развёрнутый — остальные.
    supabase
      .rpc('get_frequent_items', { p_limit: 40, p_meal_type: mealType })
      .then(({ data, error }) => {
        if (error) { console.error('[FrequentItems]', error); setItems([]); return; }
        setItems((data as FrequentItem[]) ?? []);
      });
  }, [profile?.id, mealType]);

  if (items === null) {
    return (
      <div className="flex items-center gap-2 text-xs text-ink-dim py-2">
        <Loader2 size={13} className="animate-spin" /> Смотрю, что ты обычно ешь…
      </div>
    );
  }

  const hidden = new Set(hideNames.map((n) => n.trim().toLowerCase()));
  const visible = items.filter((i) => !hidden.has(i.name.trim().toLowerCase()));

  // Новичку показывать нечего — пустой блок «часто ешь» выглядит сломанным.
  if (visible.length === 0) return null;

  const shown = open ? visible : visible.slice(0, COLLAPSED);
  const rest = visible.length - COLLAPSED;

  return (
    <div className="mb-4">
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-xs text-ink-dim">
          Обычно на {mealTypeLabel[mealType]?.toLowerCase() ?? 'этот приём'}
        </span>
        {rest > 0 && (
          <button
            onClick={() => { setOpen((v) => !v); haptic('light'); }}
            className="ml-auto text-[11px] text-emerald-glow flex items-center gap-1"
          >
            {open ? 'Свернуть' : `Ещё ${rest}`}
            <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Свёрнуто — горизонтальный ряд (не занимает экран).
          Развёрнуто — сетка: так видно всё сразу, а не по одному при прокрутке. */}
      <AnimatePresence initial={false} mode="wait">
        {open ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="grid grid-cols-2 gap-2 overflow-hidden"
          >
            {shown.map((it) => <Chip key={it.name} it={it} onPick={onPick} haptic={haptic} full />)}
          </motion.div>
        ) : (
          <motion.div
            key="row"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5"
          >
            {shown.map((it) => <Chip key={it.name} it={it} onPick={onPick} haptic={haptic} />)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Chip({
  it, onPick, haptic, full,
}: {
  it: FrequentItem;
  onPick: (i: FrequentItem) => void;
  haptic: (s?: any) => void;
  full?: boolean;
}) {
  return (
    <motion.button
      whileTap={{ scale: 0.96 }}
      onClick={() => { haptic('light'); onPick(it); }}
      className={`card px-3 py-2 text-left active:border-emerald-glow/40 transition-colors ${full ? '' : 'shrink-0'}`}
    >
      <div className="flex items-center gap-1.5">
        <Plus size={12} className="text-emerald-glow shrink-0" />
        <span className={`text-sm font-medium truncate ${full ? '' : 'max-w-[150px]'}`}>{it.name}</span>
      </div>
      <div className="text-[10px] text-ink-dim mt-0.5">
        {Math.round(Number(it.weight_g))} г · {it.calories} ккал
      </div>
    </motion.button>
  );
}
